import React, { useRef, useState } from 'react';
import css from './MobileCarousel.module.css';
import classNames from 'classnames';

interface MobileCarouselProps {
    items: React.ReactNode[];

    extraWrapperClassName?: string;
    extraInnerClassName?: string;
    extraContainerClassName?: string;
    extraItemClassName?: string;
    extraIndicatorsClassName?: string;
    extraIndicatorClassName?: string;
    extraActiveIndicatorClassName?: string;
}

const MobileCarousel: React.FC<MobileCarouselProps> = ({
    items,

    extraIndicatorClassName,
    extraActiveIndicatorClassName,
    extraIndicatorsClassName,
    extraContainerClassName,
    extraInnerClassName,
    extraItemClassName,
    extraWrapperClassName,
}) => {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleTouchStart = (e: React.TouchEvent) => {
        const touchStartX = e.touches[0].clientX;
        const handleTouchMove = (moveEvent: TouchEvent) => {
            const touchEndX = moveEvent.touches[0].clientX;
            const diffX = touchStartX - touchEndX;

            if (Math.abs(diffX) > 50) {
                if (diffX > 0 && currentIndex < items.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                }

                if (diffX < 0 && currentIndex > 0) {
                    setCurrentIndex(currentIndex - 1);
                }
                document.removeEventListener('touchmove', handleTouchMove);
            }
        };

        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener(
            'touchend',
            () => {
                document.removeEventListener('touchmove', handleTouchMove);
            },
            { once: true }
        );
    };

    const scrollToIndex = (index: number) => {
        if (carouselRef.current) {
            const itemWidth = carouselRef.current.offsetWidth;
            carouselRef.current.scrollTo({
                left: index * itemWidth,
                behavior: 'smooth',
            });
        }
    };

    React.useEffect(() => {
        scrollToIndex(currentIndex);
    }, [currentIndex]);

    const handleIndicatorClick = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <div className={classNames(css.carouselWrapper, extraWrapperClassName)}>
            <div
                className={classNames(
                    css.carouselContainer,
                    extraContainerClassName
                )}
                ref={carouselRef}
                onTouchStart={handleTouchStart}
            >
                <div
                    className={classNames(
                        css.carouselInner,
                        extraInnerClassName
                    )}
                >
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className={classNames(
                                css.carouselItem,
                                extraItemClassName
                            )}
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>
            <div
                className={classNames(css.indicators, extraIndicatorsClassName)}
            >
                {items.map((_, index) => (
                    <span
                        key={index}
                        className={classNames(
                            css.indicator,
                            extraIndicatorClassName,
                            {
                                [extraActiveIndicatorClassName
                                    ? extraActiveIndicatorClassName
                                    : css.active]: index === currentIndex,
                            }
                        )}
                        onClick={() => handleIndicatorClick(index)}
                    />
                ))}
            </div>
        </div>
    );
};

export default MobileCarousel;
